import type { AiRunStatus, DashboardCampaign } from "@adpilot/shared";
import { IconChevronRight } from "@tabler/icons-react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { MarketplaceBadge } from "@/components/shared/marketplace-badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useFormatters } from "@/lib/format";
import { cn } from "@/lib/utils";
import { AiMark, DashboardCard, DashboardCardHeader, DashboardCardLink } from "./dashboard-card";
import { wholeCurrencyOptions } from "./overview-format";

export interface TopCampaignRow {
  id: string;
  name: string;
  shopName: string | null;
  marketplace: string | null;
  aiManaged: boolean;
  spend: number;
  /** Period-over-period ratio change (0.061 = +6.1%); null hides the delta line. */
  spendChange: number | null;
  sales: number;
  salesChange: number | null;
  orders: number;
  roas: number | null;
  /** Ratio (0.213 = 21.3%). */
  acos: number | null;
  /** Spend ÷ (daily budget × period days); null without a daily budget. */
  budgetUtilization: number | null;
  /** Oldest → newest, at most 5. */
  recentRuns: AiRunStatus[];
}

interface BuildTopCampaignRowsOptions {
  periodDays: number;
  marketplaceByShop?: ReadonlyMap<string, string>;
  runsByCampaign?: ReadonlyMap<string, AiRunStatus[]>;
  limit?: number;
}

/** Highest-spend campaigns of the dashboard summary, mapped to table rows. */
export function buildTopCampaignRows(
  campaigns: readonly DashboardCampaign[],
  { periodDays, marketplaceByShop, runsByCampaign, limit = 5 }: BuildTopCampaignRowsOptions,
): TopCampaignRow[] {
  return [...campaigns]
    .sort((a, b) => b.metrics.spend - a.metrics.spend || b.metrics.sales - a.metrics.sales)
    .slice(0, limit)
    .map((campaign) => {
      const budget = campaign.dailyBudget ? campaign.dailyBudget * Math.max(1, periodDays) : null;
      const latest = campaign.latestAiRun?.status;
      return {
        id: campaign.id,
        name: campaign.name,
        shopName: campaign.shopName,
        marketplace: marketplaceByShop?.get(campaign.shopId) ?? null,
        aiManaged: campaign.aiManaged,
        spend: campaign.metrics.spend,
        // FIXME: the summary has no per-campaign previous period yet.
        spendChange: null,
        sales: campaign.metrics.sales,
        salesChange: null,
        orders: campaign.metrics.orders,
        roas: campaign.metrics.spend > 0 ? campaign.metrics.roas : null,
        acos: campaign.metrics.sales > 0 ? campaign.metrics.acos : null,
        budgetUtilization: budget ? campaign.metrics.spend / budget : null,
        recentRuns: runsByCampaign?.get(campaign.id) ?? (latest ? [latest] : []),
      };
    });
}

type AcosTone = "success" | "warning" | "destructive" | "neutral";

/** Within 10% over target still reads as on target; past 25% over is off target. */
export function acosTone(acos: number, target: number | null): AcosTone {
  if (target === null || target <= 0) return "neutral";
  if (acos <= target * 1.1) return "success";
  if (acos <= target * 1.25) return "warning";
  return "destructive";
}

const ACOS_PILL: Record<AcosTone, string> = {
  success: "bg-success-background text-success-foreground",
  warning: "bg-warning-background text-warning-foreground",
  destructive: "bg-destructive-background text-destructive-foreground",
  neutral: "bg-subtle text-text-secondary",
};

const RUN_BAR: Record<AiRunStatus, string> = {
  success: "bg-success",
  partial: "bg-warning",
  failed: "bg-destructive",
  suspended: "bg-brand",
  running: "bg-brand",
};

const RUN_SLOTS = 5;

// Fixed lanes (each width includes the 12px column gap carried as left padding).
// Full table needs 930px; Orders/ROAS (P3) drop first, then the table scrolls
// at 770px with the Campaign column pinned.
const HIDE_P3 = "@max-[930px]:hidden";
const STICKY_HEAD = "sticky left-0 z-[1] bg-table-footer";
const STICKY_CELL =
  "sticky left-0 z-[1] bg-surface group-hover/row:bg-linear-to-r group-hover/row:from-table-row-hover group-hover/row:to-table-row-hover";

function Delta({ value, positiveIsGood }: { value: number | null; positiveIsGood: boolean }) {
  const { formatPercent } = useFormatters();
  if (value === null) return null;
  const good = value === 0 ? null : value > 0 === positiveIsGood;
  const tone =
    good === null
      ? "text-muted-foreground"
      : good
        ? "text-success-foreground"
        : "text-destructive-foreground";
  const sign = value > 0 ? "+" : value < 0 ? "−" : "";
  return (
    <span className={`text-badge font-medium tabular-nums ${tone}`}>
      {sign}
      {formatPercent(Math.abs(value), 1)}
    </span>
  );
}

function RunBars({ statuses }: { statuses: AiRunStatus[] }) {
  const { t } = useTranslation("dashboard");
  const slots: Array<AiRunStatus | null> = [
    ...Array.from({ length: Math.max(0, RUN_SLOTS - statuses.length) }, () => null),
    ...statuses.slice(-RUN_SLOTS),
  ];
  const label =
    statuses.length === 0
      ? t("overview.topCampaigns.runs.none")
      : statuses.map((status) => t(`overview.topCampaigns.runs.status.${status}`)).join(", ");
  return (
    <span role="img" aria-label={label} title={label} className="flex items-center gap-0.5 pl-4">
      {slots.map((status, index) => (
        <span
          // Slots are positional (oldest → newest), so the index is the identity.
          // biome-ignore lint/suspicious/noArrayIndexKey: positional slots
          key={index}
          className={cn("h-3.5 w-[3px] rounded-[2px]", status ? RUN_BAR[status] : "bg-track")}
        />
      ))}
    </span>
  );
}

function BudgetMeter({ utilization }: { utilization: number | null }) {
  const { formatPercent } = useFormatters();
  if (utilization === null) {
    return <span className="block pl-3 text-xs text-muted-foreground">—</span>;
  }
  const capped = utilization >= 0.95;
  return (
    <span className="flex items-center gap-2 pl-3">
      <span className="h-1 min-w-0 flex-1 overflow-hidden rounded-[2px] bg-track">
        <span
          className={cn("block h-full rounded-[2px]", capped ? "bg-warning" : "bg-chart-1")}
          style={{ width: `${Math.min(1, utilization) * 100}%` }}
        />
      </span>
      <span
        className={cn(
          "w-9 shrink-0 text-right text-xs tabular-nums",
          capped ? "text-warning-foreground" : "text-muted-foreground",
        )}
      >
        {formatPercent(utilization, 0)}
      </span>
    </span>
  );
}

function ModeSlot({ aiManaged }: { aiManaged: boolean }) {
  const { t } = useTranslation("dashboard");
  const label = aiManaged
    ? t("overview.topCampaigns.mode.aiManaged")
    : t("overview.topCampaigns.mode.manual");
  return (
    <Tooltip>
      <TooltipTrigger
        render={<span className="flex size-4 shrink-0 items-center justify-center" />}
        aria-label={label}
      >
        {aiManaged ? <AiMark size="sm" /> : null}
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

export interface TopCampaignsCardProps {
  rows: readonly TopCampaignRow[];
  /** All campaigns in scope, for "View all N". */
  totalCount: number;
  /** Applied dashboard ASIN filter; "View all" opens the campaign list with it. */
  asins?: readonly string[];
  currency: string | null;
  /** Workspace target ACoS ratio; ACoS pills stay neutral without it. */
  targetAcos: number | null;
  isLoading?: boolean;
  className?: string;
}

/**
 * Paper "Top Campaigns Card", option 3 (Mode folded into Campaign): fixed column
 * lanes, P3 columns hidden by card width, horizontal scroll with a pinned
 * Campaign column as the last fallback.
 */
export function TopCampaignsCard({
  rows,
  totalCount,
  asins,
  currency,
  targetAcos,
  isLoading = false,
  className,
}: TopCampaignsCardProps) {
  const { t } = useTranslation("dashboard");
  const { formatNumber, formatPercent } = useFormatters();
  const navigate = useNavigate();
  const money = (value: number) => formatNumber(value, wholeCurrencyOptions(currency));
  const head =
    "h-8 bg-table-footer px-0 pl-3 text-xs font-medium tracking-normal text-muted-foreground";

  return (
    <DashboardCard aria-labelledby="overview-top-campaigns-title" className={className}>
      <DashboardCardHeader
        titleId="overview-top-campaigns-title"
        title={t("overview.topCampaigns.title")}
        meta={
          <span className="inline-flex items-center gap-1.5">
            <AiMark size="sm" />
            {t("overview.topCampaigns.mode.aiManaged")}
          </span>
        }
        action={
          <DashboardCardLink
            to="/campaigns"
            search={asins && asins.length > 0 ? { asins: [...asins] } : {}}
          >
            {t("overview.topCampaigns.viewAll", { count: totalCount })}
          </DashboardCardLink>
        }
      />
      <Table className="min-w-[770px] table-fixed">
        <TableHeader>
          <TableRow className="h-8 border-y border-line hover:bg-transparent">
            <TableHead className={cn(head, STICKY_HEAD, "pl-5")}>
              {t("overview.topCampaigns.columns.campaign")}
            </TableHead>
            <TableHead className={cn(head, "w-[108px] text-right")}>
              {t("overview.topCampaigns.columns.spend")}
            </TableHead>
            <TableHead className={cn(head, "w-[108px] text-right")}>
              {t("overview.topCampaigns.columns.sales")}
            </TableHead>
            <TableHead className={cn(head, HIDE_P3, "w-[84px] text-right")}>
              {t("overview.topCampaigns.columns.orders")}
            </TableHead>
            <TableHead className={cn(head, HIDE_P3, "w-[76px] text-right")}>
              {t("overview.topCampaigns.columns.roas")}
            </TableHead>
            <TableHead className={cn(head, "w-[84px] text-right")}>
              {t("overview.topCampaigns.columns.acos")}
            </TableHead>
            <TableHead className={cn(head, "w-[124px]")}>
              <span className="pl-3">{t("overview.topCampaigns.columns.budget")}</span>
            </TableHead>
            <TableHead className={cn(head, "w-[80px]")}>
              <span className="pl-4">{t("overview.topCampaigns.columns.aiRuns")}</span>
            </TableHead>
            <TableHead className={cn(head, "w-[46px]")}>
              <span className="sr-only">{t("overview.topCampaigns.columns.open")}</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            [0, 1, 2, 3].map((row) => (
              <TableRow key={row} className="h-14 border-line hover:bg-transparent">
                <TableCell colSpan={9} className="px-5">
                  <Skeleton className="h-4 w-full" />
                </TableCell>
              </TableRow>
            ))
          ) : rows.length === 0 ? (
            <TableRow className="h-14 hover:bg-transparent">
              <TableCell colSpan={9} className="px-5 text-sm text-muted-foreground">
                {t("overview.topCampaigns.empty")}
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => (
              <TableRow
                key={row.id}
                className="group/row h-14 cursor-pointer border-line hover:bg-table-row-hover"
                // Keyboard users open the row through the campaign name link.
                onClick={() => void navigate({ to: "/campaigns/$id", params: { id: row.id } })}
              >
                <TableCell className={cn(STICKY_CELL, "py-2 pr-0 pl-5")}>
                  <span className="flex min-w-0 items-center gap-2">
                    <ModeSlot aiManaged={row.aiManaged} />
                    <span className="flex min-w-0 flex-col gap-px">
                      <Link
                        to="/campaigns/$id"
                        params={{ id: row.id }}
                        onClick={(event) => event.stopPropagation()}
                        className="truncate rounded-xs text-sm font-medium text-ink outline-none focus-visible:ring-2 focus-visible:ring-focus-ring"
                      >
                        {row.name}
                      </Link>
                      {row.shopName || row.marketplace ? (
                        <span className="flex min-w-0 items-center gap-1.5">
                          {row.shopName ? (
                            <span className="truncate text-xs text-muted-foreground">
                              {row.shopName}
                            </span>
                          ) : null}
                          {row.marketplace ? (
                            <MarketplaceBadge size="sm" code={row.marketplace} />
                          ) : null}
                        </span>
                      ) : null}
                    </span>
                  </span>
                </TableCell>
                <TableCell className="py-2 pr-0 pl-3 text-right">
                  <span className="flex flex-col items-end gap-px">
                    <span className="text-sm tabular-nums text-ink">{money(row.spend)}</span>
                    <Delta value={row.spendChange} positiveIsGood={false} />
                  </span>
                </TableCell>
                <TableCell className="py-2 pr-0 pl-3 text-right">
                  <span className="flex flex-col items-end gap-px">
                    <span className="text-sm tabular-nums text-ink">{money(row.sales)}</span>
                    <Delta value={row.salesChange} positiveIsGood />
                  </span>
                </TableCell>
                <TableCell
                  className={cn(HIDE_P3, "py-2 pr-0 pl-3 text-right text-sm tabular-nums text-ink")}
                >
                  {formatNumber(row.orders, { maximumFractionDigits: 0 })}
                </TableCell>
                <TableCell
                  className={cn(HIDE_P3, "py-2 pr-0 pl-3 text-right text-sm tabular-nums text-ink")}
                >
                  {row.roas === null
                    ? "—"
                    : formatNumber(row.roas, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                </TableCell>
                <TableCell className="py-2 pr-0 pl-3 text-right">
                  {row.acos === null ? (
                    <span className="text-sm text-muted-foreground">—</span>
                  ) : (
                    <span
                      className={cn(
                        "inline-flex h-5.5 items-center rounded-full px-1.75 text-xs font-medium tabular-nums",
                        ACOS_PILL[acosTone(row.acos, targetAcos)],
                      )}
                    >
                      {formatPercent(row.acos, 1)}
                    </span>
                  )}
                </TableCell>
                <TableCell className="py-2 pr-0 pl-3">
                  <BudgetMeter utilization={row.budgetUtilization} />
                </TableCell>
                <TableCell className="py-2 pr-0 pl-3">
                  <RunBars statuses={row.recentRuns} />
                </TableCell>
                <TableCell className="py-2 pr-5 pl-3">
                  <IconChevronRight
                    aria-hidden
                    className="size-3.5 text-muted-foreground"
                    strokeWidth={1.75}
                  />
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </DashboardCard>
  );
}
